/**
 * WCAG 2.1 Accessibility Utilities
 * Provides helper functions for accessibility compliance
 */

/**
 * Announce message to screen readers
 * @param message - Message to announce
 * @param priority - 'polite' (default) or 'assertive'
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div')
  announcement.setAttribute('role', 'status')
  announcement.setAttribute('aria-live', priority)
  announcement.setAttribute('aria-atomic', 'true')
  announcement.className = 'sr-only'
  announcement.textContent = message

  document.body.appendChild(announcement)

  setTimeout(() => {
    document.body.removeChild(announcement)
  }, 1000)
}

/**
 * Trap focus within a container (for modals, drawers)
 * @param container - Container element
 * @returns Cleanup function
 */
export const trapFocus = (container: HTMLElement): (() => void) => {
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )

  const firstFocusable = focusableElements[0]
  const lastFocusable = focusableElements[focusableElements.length - 1]

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault()
        lastFocusable?.focus()
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault()
        firstFocusable?.focus()
      }
    }
  }

  container.addEventListener('keydown', handleKeyDown)

  // Focus first element
  firstFocusable?.focus()

  return () => {
    container.removeEventListener('keydown', handleKeyDown)
  }
}

/**
 * Generate unique ID for ARIA labels
 * @param prefix - Prefix for the ID
 * @returns Unique ID
 */
let idCounter = 0
export const generateAriaId = (prefix = 'aria'): string => {
  idCounter += 1
  return `${prefix}-${idCounter}-${Date.now()}`
}

/**
 * Check if element is visible and focusable
 * @param element - Element to check
 * @returns Whether element is focusable
 */
export const isFocusable = (element: HTMLElement): boolean => {
  if (
    element.hasAttribute('disabled') ||
    element.getAttribute('aria-hidden') === 'true'
  ) {
    return false
  }

  const style = window.getComputedStyle(element)
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false
  }

  return true
}

/**
 * Get all focusable elements within a container
 * @param container - Container element
 * @returns Array of focusable elements
 */
export const getFocusableElements = (
  container: HTMLElement = document.body
): HTMLElement[] => {
  const elements = Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  )

  return elements.filter(isFocusable)
}

/**
 * Manage focus restoration
 */
export class FocusManager {
  private previousFocus: HTMLElement | null = null

  capture(): void {
    this.previousFocus = document.activeElement as HTMLElement
  }

  restore(): void {
    if (this.previousFocus && isFocusable(this.previousFocus)) {
      this.previousFocus.focus()
    }
  }
}

/**
 * Format file size for screen readers
 * @param bytes - File size in bytes
 * @returns Human-readable file size
 */
export const formatFileSizeForScreenReader = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'Kilobytes', 'Megabytes', 'Gigabytes']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Format date for screen readers (more verbose)
 * @param date - Date to format
 * @returns Screen reader friendly date
 */
export const formatDateForScreenReader = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Create ARIA label for data grid cells
 * @param value - Cell value
 * @param columnName - Column name
 * @param rowIndex - Row index
 * @returns ARIA label
 */
export const createGridCellAriaLabel = (
  value: string | number,
  columnName: string,
  rowIndex: number
): string => {
  return `${columnName}: ${value}, Row ${rowIndex + 1}`
}

/**
 * Handle keyboard navigation for custom components
 * @param event - Keyboard event
 * @param handlers - Object with handler functions for different keys
 */
export const handleKeyboardNavigation = (
  event: React.KeyboardEvent,
  handlers: {
    onEnter?: () => void
    onSpace?: () => void
    onEscape?: () => void
    onArrowUp?: () => void
    onArrowDown?: () => void
    onArrowLeft?: () => void
    onArrowRight?: () => void
    onHome?: () => void
    onEnd?: () => void
  }
): void => {
  const { key } = event

  switch (key) {
    case 'Enter':
      handlers.onEnter?.()
      break
    case ' ':
    case 'Spacebar':
      event.preventDefault()
      handlers.onSpace?.()
      break
    case 'Escape':
      handlers.onEscape?.()
      break
    case 'ArrowUp':
      event.preventDefault()
      handlers.onArrowUp?.()
      break
    case 'ArrowDown':
      event.preventDefault()
      handlers.onArrowDown?.()
      break
    case 'ArrowLeft':
      handlers.onArrowLeft?.()
      break
    case 'ArrowRight':
      handlers.onArrowRight?.()
      break
    case 'Home':
      event.preventDefault()
      handlers.onHome?.()
      break
    case 'End':
      event.preventDefault()
      handlers.onEnd?.()
      break
  }
}

/**
 * Debounce function for search inputs (reduces screen reader announcements)
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounceForAccessibility = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Check color contrast ratio (WCAG AA requires 4.5:1 for normal text)
 * @param color1 - First color in hex
 * @param color2 - Second color in hex
 * @returns Contrast ratio
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const getLuminance = (hexColor: string): number => {
    const rgb = parseInt(hexColor.slice(1), 16)
    const r = ((rgb >> 16) & 0xff) / 255
    const g = ((rgb >> 8) & 0xff) / 255
    const b = (rgb & 0xff) / 255

    const [rs, gs, bs] = [r, g, b].map((c) => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
  }

  const lum1 = getLuminance(color1)
  const lum2 = getLuminance(color2)

  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)

  return (lighter + 0.05) / (darker + 0.05)
}
